import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  GET_HOST_WITH_ALERTS,
  GET_VISIBILITY,
  GET_CRITICALITY,
  sensitivityMap,
  MonitorMap,
  desiredOrderAlerts,
  desiredOrderMonitor,
  desiredOrderCriticality,
  criticalityBGColorMap,
  criticalityMap,
  GET_OS_TYPES,
  GET_SOURCE_TYPES,
} from "./NetSpaceConstant";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import { Grid } from "material-ui-core";
import CardContent from "@material-ui/core/CardContent";
import {
  alertColor,
  fallbackColors,
  formatNumberUS,
  osTypesColors,
  sensitivityColorDark1,
  sourceNameMap,
  sourceTypesColors,
  StyledCardHeader,
} from "./NetSpaceUtils";
import { CustomCard } from "./NetSpaceCommonComp";
import { PieChart, Pie, Tooltip, Cell, ResponsiveContainer } from "recharts";
import { useMediaQuery } from "@material-ui/core";

function CombinedHostWidget() {
  const dispatch = useDispatch();
  const isSmall = useMediaQuery("(max-width:1600px)");
  const isMedium = useMediaQuery("(max-width:1800px)");

  const [selectedKey, setSelectedKey] = useState("visibility");

  useEffect(() => {
    dispatch({ type: GET_OS_TYPES });
    dispatch({ type: GET_VISIBILITY });
    dispatch({ type: GET_CRITICALITY });
    dispatch({ type: GET_SOURCE_TYPES });
  }, [dispatch]);

  const visibility = useSelector((state) => state.NetspaceReducer.visibility);
  const criticality = useSelector((state) => state.NetspaceReducer.criticality);
  const osTypes = useSelector((state) => state.NetspaceReducer.osTypes);
  const sourceTypes = useSelector((state) => state.NetspaceReducer.sourceTypes);

  const loaderOsTypes = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_OS_TYPES],
  );
  const loaderVisibility = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_VISIBILITY],
  );
  const loaderCriticality = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_CRITICALITY],
  );
  const loaderSourceTypes = useSelector(
    (state) => state.NetspaceReducer.componentLoader[GET_SOURCE_TYPES],
  );

  const getTopN = (data = [], n = 5) => {
    return [...data].sort((a, b) => b.value - a.value).slice(0, n);
  };


  const totalOSTypes = osTypes
    ? Object.values(osTypes).reduce((a, b) => a + b, 0)
    : 0;

  const totalVisibility = visibility
    ? Object.values(visibility).reduce((a, b) => a + b, 0)
    : 0;

  const totalCriticality = criticality
    ? desiredOrderCriticality.reduce(
        (acc, type) => acc + (criticality[type] || 0),
        0,
      )
    : 0;

  const totalSourceTypes = sourceTypes
    ? Object.values(sourceTypes).reduce((a, b) => a + b, 0)
    : 0;

  // data prep

  const osTypesData = useMemo(() => {
    return Object.keys(osTypes || {})
      .map((type) => ({
        name: type,
        value: osTypes[type],
      }))
      .sort((a, b) => b.value - a.value)
      .map((item, index) => ({
        ...item,
        color:
          osTypesColors(item.name) ||
          fallbackColors[index % fallbackColors.length],
      }));
  }, [osTypes]);

  const osTypesTop5 = useMemo(() => getTopN(osTypesData, 5), [osTypesData]);

  const visibilityData = useMemo(
    () =>
      desiredOrderMonitor
        .map((monitor) => ({
          name: `${MonitorMap[monitor]} Visibility`,
          value: visibility?.[monitor] || 0,
          color: sensitivityColorDark1(monitor),
        }))
        .sort((a, b) => b.value - a.value),
    [visibility],
  );

  const criticalityData = useMemo(
    () =>
      desiredOrderCriticality
        .map((type) => ({
          name: `${criticalityMap[type]} Criticality`,
          value: criticality?.[type] || 0,
          color: criticalityBGColorMap[type],
        }))
        .sort((a, b) => b.value - a.value),
    [criticality],
  );

 const sourceTypesData = useMemo(() => {
   return Object.keys(sourceTypes || {})
     .map((type) => ({
       name: sourceNameMap[type] || type,
       value: sourceTypes[type],
     }))
     .sort((a, b) => b.value - a.value)
     .map((item, index) => ({
       ...item,
       color:
         sourceTypesColors(item.name) ||
         fallbackColors[index % fallbackColors.length],
     }));
 }, [sourceTypes]);

  // responsive helper
  const getSize = (small, medium, normal) => {
    if (isSmall) return small;
    if (isMedium) return medium;
    return normal;
  };

  // CONFIG
  const widgetConfig = useMemo(
    () => [
      {
        key: "visibility",
        title: "Visibility",
        total: totalVisibility,
        data: visibilityData,
      },
      {
        key: "criticality",
        title: "Critical Ports",
        total: totalCriticality,
        data: criticalityData,
      },
      {
        key: "osTypes",
        title: "OS Types",
        total: totalOSTypes,
        data: osTypesData,
      },
      {
        key: "sourceTypes",
        title: "Agents",
        total: totalSourceTypes,
        data: sourceTypesData,
      },
      // future categories go here
    ],
    [
      totalVisibility,
      totalCriticality,
      visibilityData,
      criticalityData,
      totalOSTypes,
      osTypesData,
    ],
  );

  // FILTER ZERO DATA WIDGETS
  const filteredWidgets = useMemo(() => {
    return widgetConfig.filter((widget) =>
      widget.data?.some((item) => item.value > 0),
    );
  }, [widgetConfig]);

  // FIX SELECTED TAB IF REMOVED
  useEffect(() => {
    if (!filteredWidgets.find((w) => w.key === selectedKey)) {
      setSelectedKey(filteredWidgets[0]?.key);
    }
  }, [filteredWidgets, selectedKey]);

  const selectedWidget =
    filteredWidgets.find((w) => w.key === selectedKey) ??
    filteredWidgets[0] ??
    null;

    const displayData =
      selectedWidget?.key === "osTypes"
        ? osTypesTop5
        : selectedWidget?.data || [];

  const totalHosts =
    totalVisibility ||
    totalCriticality ||
    totalOSTypes ||
    totalSourceTypes ||
    0;

  return (
    <Grid xs={3} item>
      <CustomCard color="white">
        <StyledCardHeader
          title={
            <div className="combined-host-container">
              <span className="host-header">
                Host Overview: {formatNumberUS(totalHosts)}
              </span>
              <div className="widget-filter">
                {filteredWidgets.map((w) => (
                  <span
                    key={w.key}
                    onClick={() => setSelectedKey(w.key)}
                    className="widget-container"
                    style={{
                      background:
                        selectedKey === w.key ? "rgb(71, 133, 109)" : "#fff",
                      color: selectedKey === w.key ? "#fff" : "#222",
                      boxShadow:
                        selectedKey === w.key
                          ? "0 3px 8px rgba(71, 133, 109, 0.4)"
                          : undefined,
                    }}
                  >
                    {w.title}
                  </span>
                ))}
              </div>
            </div>
          }
        />

        <CardContent>
          {loaderOsTypes || loaderVisibility || loaderCriticality ? (
            <img src={Page_Loader} alt="loading" className="loader-img" />
          ) : filteredWidgets.length === 0 ? (
            <div className="no-data">No data available</div>
          ) : (
            <div className="combined-host-widget">
              <div className="combined-host-widget__text">
                {/* KEEP THIS COMMENTED BLOCK AS IS */}
                {/*
                <div className="text-info-container">
                  <span className="text-info-title">
                    Alerts: {totalAlerts}
                  </span>
                </div>
                */}

                <div className="text-info-container">
                  {/* <span
                    className="text-info-title"
                    title={`${selectedWidget.title}: ${selectedWidget.total}`}
                  >
                    {`${selectedWidget.title}: ${selectedWidget.total}`}
                  </span> */}

                  {displayData
                    .filter((item) => item.value > 0)
                    .map((item, index) => (
                      <div
                        key={index}
                        className="text-ellipsis"
                        style={{ color: item.color }}
                      >
                        {formatNumberUS(item.value)}{" "}
                        {item.name.replace(selectedWidget.title, "")}
                      </div>
                    ))}
                </div>
              </div>

              <div className="combined-host-widget__chart">
                <ResponsiveContainer width={130} height={130}>
                  <PieChart>
                    <Pie
                      data={selectedWidget.data}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={getSize(30, 40, 50)}
                    >
                      {selectedWidget.data.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>

                    <Tooltip
                      content={({ active, payload }) =>
                        active && payload?.length ? (
                          <div
                            style={{
                              background: "#fff",
                              padding: "5px",
                              border: "1px solid #ccc",
                              fontSize: getSize(10, 11, 12),
                            }}
                          >
                            {payload.map((entry, idx) => (
                              <div key={idx}>
                                {entry.name}: {entry.value}
                              </div>
                            ))}
                          </div>
                        ) : null
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </CardContent>
      </CustomCard>
    </Grid>
  );
}

export default CombinedHostWidget;
